import {DrawFrmApp} from "../../drawfrm/DrawFrmApp";
import {ManagedScene2} from "../ManagedScene2";
import {FrmSceneLayout} from "../../drawfrm/FrmSceneLayout";
import {ElevationApp} from "../../ElevationApp";
import {AddFx, ChildView} from "../../shared-2d/ChildView";
import {DoorLeaf} from "./DoorLeaf";
import {DoorLiteThing} from "./DoorLiteThing";
import {MdDoorLite} from "../../model/door/MdDoorLite";
import {MdHardwarePlacement} from "../../model/hardware/MdHardware";
import {HardwareCrosshairThing} from "../hardware/HardwareCrosshairThing";
import {drawnOnDoorElev} from "../hardware/hardwarePreps";
import {ExportDoorDimensions} from "./ExportDoorDimensions";

// The door-elevation view: draws a single door leaf (active or inactive) with its
// hinges + handle, and any glass lites cut into it. `active` selects which leaf a
// pair shows (and which leaf's lites are drawn / added to).
export class DoorElevView extends ChildView {
    active: boolean;
    leaf: DoorLeaf;

    // engineering-drawing dimensions stacked around the leaf (overall size, lites,
    // door hardware), drawn only when the export view opts in (externalDims). Null for
    // the normal editor door view, which uses the inline lite/hardware dimensions.
    private exportDims: ExportDoorDimensions | null = null;

    // lites live as children of the leaf's graphic, so they pan/scale with it and
    // are in the leaf's local coordinate space. Tracked here so they can be torn
    // down and rebuilt when the lite set changes.
    private liteThings: DoorLiteThing[] = [];

    // the primary handle + lock preps, drawn as bored holes. Live as children of
    // the leaf, rebuilt when the placed-hardware list changes. Every other placed
    // item is left to the hardware elevation.
    private crosshairThings: HardwareCrosshairThing[] = [];

    constructor(elev: ElevationApp, add: AddFx, active: boolean, opts?: {externalDims?: boolean}) {
        super(elev, add);
        this.active = active;

        // show the over/under cuts (red hatched region + editable dimension) on the
        // leaf, in both the interactive door view and the submittal render.
        this.leaf = new DoorLeaf(elev, active, {showCuts: true});
        this.add(this.leaf);

        // the export view draws its dimensions stacked around the leaf instead of the
        // inline lite/hardware ones (those stay hidden — dimShowAll isn't set).
        if(opts?.externalDims) {
            this.exportDims = new ExportDoorDimensions(this);
        }

        this.buildLites();
        this.buildHardwareItems();

        // rebuild when a lite is added/removed.
        this.trackSubscription(elev.doorSpec.litesChanged.subscribe(() => {
            this.buildLites();
            this.redraw();
            this.elev.draw.currentScene?.applyLayout();
        }));

        // rebuild the door crosshairs when the placed-hardware list changes.
        this.trackSubscription(elev.hardware.listChanged.subscribe(() => {
            this.buildHardwareItems();
            this.redraw();
        }));

        // redraw the leaf + lites when any leaf-geometry input changes.
        const redraw = () => this.redraw();
        this.trackSubscription(elev.doorSpec.actualHeight.subscribe(redraw));
        this.trackSubscription((active ? elev.doorSpec.activeDoorWidth : elev.doorSpec.inActiveDoorWidth).subscribe(redraw));
        // repaint the cut markers + dimensions when a cut changes.
        this.trackSubscription(elev.doorSpec.overcut.subscribe(redraw));
        this.trackSubscription(elev.doorSpec.undercut.subscribe(redraw));
        this.trackSubscription(elev.hardware.hinges.listChanged.subscribe(redraw));
        // hardware crosshairs (handle/lock/generic) self-redraw off their own x/y
        // subscriptions, so no scene-level position subscription is needed here.

        // note: the selected-lite highlight is repainted by each DoorLiteThing's
        // own selection subscription (using the emitted value, to avoid the
        // lastValue lag), so no scene-level selection redraw is needed here.

        // toggle every lite's dimensions when "Show All Dimensions" changes.
        this.trackSubscription(elev.viewSettings.dimShowAll.subscribe(redraw));

        // the export dimensions aren't self-drawing (unlike the leaf/lites/crosshairs,
        // which paint in their own ctors), so paint them once up front.
        this.exportDims?.draw();
    }

    private leafSize = () => ({width: this.leaf.leafWidth(), height: this.leaf.leafHeight()});

    private buildLites() {
        for (const t of this.liteThings) {
            if (!t.destroyed) t.destroy();
        }
        this.liteThings = [];

        const {width, height} = this.leafSize();
        const leaf = this.active ? "active" : "inactive";
        for (const lite of this.elev.doorSpec.litesFor(leaf)) {
            // resolve the lite against this leaf's actual size before the first draw, so locked
            // offsets (e.g. right/bottom) are honoured even when the door differs from the design's —
            // the subscription-driven redraw only fires on later changes, not this initial build.
            lite.normalize(width, height);
            const thing = new DoorLiteThing({
                elev: this.elev,
                lite,
                leafSize: this.leafSize,
                onEdit: () => {
                    this.redraw();
                    this.elev.draw.currentScene?.applyLayout();
                },
                onSelect: (additive) => this.selectLite(lite, additive),
            });
            this.leaf.lines.addChild(thing.graphic);
            this.liteThings.push(thing);
        }
    }

    // the lites this view drew, each already resolved against the leaf it was built for
    // (see buildLites). The submittal export reads these rather than the stored design, so a
    // locked offset on a resized door reports the size actually drawn.
    measuredLites(): MdDoorLite[] {
        return this.liteThings.map(t => t.getLite());
    }

    private selectLite(lite: MdDoorLite, additive: boolean) {
        this.elev.viewSettings.selectItem({type: "door-lite", id: lite.id, leaf: lite.leaf}, additive);
        this.elev.viewSettings.activeTab.emit("selected");
    }

    // (re)build the door prep Things from the placed-hardware list. Destroy old
    // ones first so removed items leave no orphan.
    private buildHardwareItems() {
        for (const t of this.crosshairThings) {
            if (!t.destroyed) t.destroy();
        }
        this.crosshairThings = [];

        const hw = this.elev.hardwareGroup?.hardware ?? [];
        // this leaf's own handle/lock preps (an item on both leaves has one per leaf, so each shows on
        // its own leaf, positioned/dragged independently).
        for (const placement of this.elev.hardware.list.filter(p => drawnOnDoorElev(p, this.active))) {
            const item = hw.find(h => h.product === placement.productId);
            if (!item) continue; // product not in the group anymore — skip

            const thing = new HardwareCrosshairThing({
                elev: this.elev,
                item: placement,
                label: item.productCode.slice(0, 6),
                product: item,
                boreOnly: true,
                // door preps measure to the door leaf box (fixed rect).
                target: {rect: () => ({x0: 0, y0: 0, x1: this.leaf.leafWidth(), y1: this.leaf.leafHeight()})},
                onSelect: (additive) => this.selectHardwareItem(placement, additive),
            });
            this.leaf.lines.addChild(thing.graphic);
            this.crosshairThings.push(thing);
        }
    }

    private selectHardwareItem(item: MdHardwarePlacement, additive: boolean) {
        this.elev.viewSettings.selectItem({type: "hardware-item", id: item.id, kind: item.target, productId: item.productId}, additive);
        this.elev.viewSettings.activeTab.emit("selected");
    }

    redraw() {
        this.leaf.draw();
        const {width, height} = this.leafSize();
        for (const t of this.liteThings) {
            t.getLite().normalize(width, height);
            t.draw();
        }
        for (const t of this.crosshairThings) {
            t.draw();
        }
        this.exportDims?.draw();
    }

    getLayout() {
        return FrmSceneLayout.group([this.leaf]);
    }

    destroy() {
        // Always destroy the lite + hardware things: normal scene teardown frees
        // the leaf first, but its graphic.destroy({children:true}) only frees the
        // pixi graphics — it does NOT run each Thing's destroy(), so their
        // selection subscriptions would leak. Thing.destroy() is a no-op the
        // second time and skips the pixi free if the leaf already did it.
        for (const t of [...this.liteThings, ...this.crosshairThings]) {
            if (!t.destroyed) t.destroy();
        }
        this.liteThings = [];
        this.crosshairThings = [];
        // the leaf's pixi tree destroy (children:true) frees the stack's graphics; its
        // own destroy() guards the double-free.
        this.exportDims?.destroy();
        super.destroy();
    }
}

export class DoorElevScene extends ManagedScene2 {
    private active: boolean;

    constructor(app: ElevationApp, active: boolean) {
        super(app);
        this.active = active;
    }

    setup(app: DrawFrmApp): void {
        super.setup(app);

        const view = this.track(new DoorElevView(this.elev, item => this.add(item), this.active));

        this.setupLayout(
            FrmSceneLayout.column(
                view.getLayout().grow(1),
            )
                .grow(1)
                .gap(20)
                .padding(20),
        );
    }
}
